/**
 * Display-only sanitising of security product notes, so no customer-facing
 * surface ever prints the word "alarm" (Jaron, Monday 12824335937: "no puede
 * aparecer que vendemos alarmas").
 *
 * These three used to be private consts inside `react-utils.tsx`, reachable
 * only from `useSecurityScopeDetailLines` — i.e. only the CE/MSE contract's
 * Scope of Work. That is exactly how B132 happened: the contract came out
 * clean while the Job Summary, rendering the same products from the same
 * `securityAutoNotes.ts` text, printed "Alarm monitoring service (if
 * selected) billed separately" and "Solar S340 Camera" straight through.
 * They live here now so every surface shares one implementation instead of
 * each one being fixed separately (V61).
 *
 * Nothing here touches stored data. `securityAutoNotes.ts` and the Firebase
 * `_notes` fields keep their raw text — this only filters what is rendered,
 * which is what makes it fix every existing customer at once with no
 * migration.
 */

/**
 * Lines mentioning any of these are dropped from a rendered note.
 *
 * `solar` is in the list because two camera product names carry it for
 * historical reasons (see the overrides below) — which is also why this must
 * only ever be applied to security fields. Run it over a Solar efficiency's
 * notes and it would gut legitimate product description.
 */
const BANNED_LINE =
  /\b(alarm|siren|sensors?|detectors?|solar)\b|\barm\s*\/?\s*disarm\b|\bsecurity system\b/i

/**
 * Display-only product renames (2026-07-30, Maria G checklist for Olga Dias):
 * "Solar S4 Camera" -> "eufy S4 Wireless Camera", and by the same
 * eufy-branding logic "Solar S340 Camera" -> "SoloCam S340", matching the
 * already-unbranded "SoloCam S220" in the same dropdown. The raw Firebase
 * `type` is untouched.
 */
export const ALARM_SAFE_PRODUCT_NAME_OVERRIDES: Readonly<
  Record<string, string>
> = {
  'Solar S4 Camera': 'eufy S4 Wireless Camera',
  // Same product, second catalogue label — found 2026-08-25 by sweeping all 41
  // `securityAutoNotes.ts` keys for banned words instead of trusting the
  // hand-written override list, which had missed this one.
  'Solar S4 Wireless Cam': 'eufy S4 Wireless Camera',
  'Solar S340 Camera': 'SoloCam S340',
  // Legacy value — no longer in `Misc/env/security_wifi_cameras`'s dropdown
  // (only stored on older customer rows, e.g. Olga Dias) but still needs the
  // same rename per Maria G's original checklist.
  'SolarCam S3 Pro': 'eufy S3 Pro Outdoor Camera',
}

export const resolveAlarmSafeProductName = (type: string): string =>
  ALARM_SAFE_PRODUCT_NAME_OVERRIDES[type] ?? type

/** Drops whole lines that mention alarm equipment. Used where the product
 * name is already a separate field (the contract's Scope of Work). */
export const stripAlarmMentions = (notes: string): string =>
  notes
    .split('\n')
    .filter((line) => !BANNED_LINE.test(line))
    .join('\n')

const applyNameOverrides = (text: string): string =>
  Object.entries(ALARM_SAFE_PRODUCT_NAME_OVERRIDES).reduce(
    (acc, [from, to]) => acc.split(from).join(to),
    text,
  )

/**
 * The one word that takes a whole product out of a customer-facing note, no
 * matter where it appears. Narrower than `BANNED_LINE` on purpose — see the
 * header branch in `alarmSafeNotesBlock`.
 */
const LITERAL_ALARM = /\balarms?\b/i

/** A rolled-up `_notes` product header, e.g. `2 x Solar S340 Camera (…`. */
const HEADER_LINE = /^\s*\d+\s*x\s/i

/**
 * Sanitises a whole rolled-up `_notes` blob — the format built by
 * `helpers.ts`' security calculators:
 *
 *     `${qty} x ${type}` + ` (${securityAutoNotes[type]})`
 *
 * with a blank line between products.
 *
 * The header line gets special treatment instead of being filtered like the
 * rest. Dropping it would take the quantity and the product with it, so a
 * customer who bought two cameras would simply stop seeing them — worse than
 * the bug being fixed. Instead the header keeps `N x <renamed product>` and
 * loses the parenthesised note title, whose own first line carries the
 * banned words ("SoloCam S340 Solar Camera —").
 */
export const alarmSafeNotesBlock = (text: string): string =>
  applyNameOverrides(text)
    .split('\n\n')
    .map((block) => {
      const lines = block.split('\n')
      const out: string[] = []

      let dropWholeProduct = false

      lines.forEach((line, i) => {
        if (i === 0 && HEADER_LINE.test(line)) {
          // Keep `N x Name`, drop the `(note title —` that follows it.
          const open = line.indexOf('(')
          const header = (open === -1 ? line : line.slice(0, open)).trimEnd()

          // A header is held to a narrower rule than a bullet. 13 of the 41
          // catalogue products are named "Door Sensor", "Motion Detector" and
          // the like: that is the equipment the customer actually bought, and
          // naming it is not the same as saying we sell alarms. Only a product
          // whose name literally says "alarm" takes its whole block out —
          // that one word is the thing Jaron banned outright.
          if (LITERAL_ALARM.test(header)) {
            dropWholeProduct = true
            return
          }
          out.push(header)
          return
        }
        if (!BANNED_LINE.test(line)) out.push(line)
      })

      if (dropWholeProduct) return ''

      // The blob wrapped every note in parentheses; with the opening one gone
      // the trailing one would dangle off the last surviving bullet.
      const last = out.length - 1
      if (last > 0 && out[last]!.endsWith(')')) {
        out[last] = out[last]!.slice(0, -1)
      }

      return out.join('\n').trim()
    })
    .filter((block) => block.length > 0)
    .join('\n\n')

/**
 * Joins a product blob with the rep's own free-text notes before
 * `alarmSafeNotesBlock` sees them.
 *
 * The separator is the whole point. Products inside the blob are separated by
 * a BLANK line, so joining the rep's notes with a single '\n' put their text
 * inside the last product's block — where the trailing-bracket cleanup ate its
 * last character, and the product's own dangling bracket survived in its place
 * because it was no longer the last line (both found 2026-08-26).
 *
 * Lives here, not inline in `Cost.tsx`, so the separator is testable and so the
 * next surface that renders these two fields together cannot get it wrong on
 * its own — V62.
 */
export const joinProductNotes = (notes: string, extraNotes: string): string =>
  [notes, extraNotes]
    .map((s) => (s || '').trim())
    .filter((s) => s.length > 0)
    .join('\n\n')
